import { useState, useCallback, useRef, useEffect } from 'react';

const useAIFeedback = (classContext = null) => {
  // Start with empty feedback - will be populated when file is opened
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false); // Start with feedback pane closed

  // Track snapshots for intelligent triggering
  const [snapshots, setSnapshots] = useState([]);
  const lastAnalysisTime = useRef(0);
  const analysisTimer = useRef(null);

  // Mock AI feedback service - replace with actual API calls later
  const analyzeCodeSnapshot = useCallback(async (code, language, activeFile) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000));

    // Mock AI analysis based on code content
    const lines = code.split('\n');
    const mockFeedback = [];

    lines.forEach((line, index) => {
      const lineNum = index + 1;
      const trimmedLine = line.trim();

      // Mock feedback rules
      if (trimmedLine.includes('TODO') || trimmedLine.includes('pass')) {
        mockFeedback.push({
          id: `feedback_${Date.now()}_${lineNum}`,
          type: 'warning',
          line: lineNum,
          message: 'Consider implementing this functionality',
          details: 'This appears to be placeholder code. Implementing the actual logic here will improve your program\'s functionality.',
          code: trimmedLine
        });
      }

      if (trimmedLine.includes('def ') && !trimmedLine.includes('self') && language === 'python') {
        mockFeedback.push({
          id: `feedback_${Date.now()}_${lineNum}_method`,
          type: 'suggestion',
          line: lineNum,
          message: 'Consider if this should be a class method',
          details: 'If this function operates on instance data, consider making it a method of a class with \'self\' parameter.',
          code: trimmedLine,
          example: 'def move_piece(self, from_pos, to_pos):\n    # Implementation here'
        });
      }

      if (trimmedLine.includes('class ') && language === 'python') {
        mockFeedback.push({
          id: `feedback_${Date.now()}_${lineNum}_class`,
          type: 'success',
          line: lineNum,
          message: 'Good use of object-oriented programming!',
          details: 'Classes help organize your code and make it more maintainable. Consider what attributes and methods this class needs.',
          code: trimmedLine
        });
      }

      if (trimmedLine.includes('for ') && trimmedLine.includes('range(')) {
        mockFeedback.push({
          id: `feedback_${Date.now()}_${lineNum}_loop`,
          type: 'suggestion',
          line: lineNum,
          message: 'Consider loop optimization',
          details: 'For better performance with large datasets, consider if there\'s a more efficient approach or if you can use list comprehensions.',
          code: trimmedLine,
          example: '# List comprehension alternative:\nresult = [process(item) for item in items]'
        });
      }

      if (trimmedLine.includes('print(') && !trimmedLine.includes('debug')) {
        mockFeedback.push({
          id: `feedback_${Date.now()}_${lineNum}_print`,
          type: 'suggestion',
          line: lineNum,
          message: 'Consider using logging instead of print',
          details: 'Using logging gives you better control over output levels and formatting in larger applications.',
          code: trimmedLine,
          example: 'import logging\nlogging.info("Your message here")'
        });
      }
    });

    // Add some assignment-specific feedback for chess game
    if (activeFile?.file?.name?.toLowerCase().includes('chess') ||
        code.toLowerCase().includes('chess') ||
        code.toLowerCase().includes('board')) {

      if (!code.includes('__init__')) {
        mockFeedback.push({
          id: `feedback_chess_init`,
          type: 'suggestion',
          line: 1,
          message: 'Chess game needs initialization',
          details: 'A chess game should initialize the board with pieces in their starting positions, track the current player, and maintain game state.',
          example: 'def __init__(self):\n    self.board = self.initialize_board()\n    self.current_player = "white"\n    self.game_over = False'
        });
      }

      if (!code.includes('move') && !code.includes('Move')) {
        mockFeedback.push({
          id: `feedback_chess_move`,
          type: 'warning',
          line: 1,
          message: 'Missing move functionality',
          details: 'A chess game needs methods to handle piece movement, including validation of legal moves.',
          example: 'def move_piece(self, from_pos, to_pos):\n    # Validate move\n    # Update board\n    # Switch players'
        });
      }
    }

    return mockFeedback;
  }, []);

  // Trigger analysis with intelligent timing
  const triggerAnalysis = useCallback(async (code, language, activeFile, trigger = 'manual') => {
    if (!code || !activeFile) return;

    const now = Date.now();

    // Prevent too frequent analysis
    if (trigger === 'auto' && now - lastAnalysisTime.current < 10000) {
      return; // Wait at least 10 seconds between auto-analyses
    }

    try {
      setLoading(true);
      setError(null);
      lastAnalysisTime.current = now;

      const feedback = await analyzeCodeSnapshot(code, language, activeFile);

      setFeedbackItems(feedback);

      // Don't automatically open the feedback pane - let inline decorations show by default

      // Add to snapshots for tracking
      setSnapshots(prev => [
        ...prev.slice(-4), // Keep last 5 snapshots
        {
          timestamp: now,
          code,
          language,
          file: activeFile?.file?.name,
          feedbackCount: feedback.length,
          trigger
        }
      ]);

    } catch (err) {
      setError(err.message);
      console.error('AI feedback analysis failed:', err);
    } finally {
      setLoading(false);
    }
  }, [analyzeCodeSnapshot]);

  // Debounced auto-analysis
  const scheduleAutoAnalysis = useCallback((code, language, activeFile) => {
    if (analysisTimer.current) {
      clearTimeout(analysisTimer.current);
    }

    // Trigger analysis after 30 seconds of inactivity
    analysisTimer.current = setTimeout(() => {
      triggerAnalysis(code, language, activeFile, 'auto');
    }, 30000);
  }, [triggerAnalysis]);

  // Check if we should trigger based on code changes
  const analyzeCodeChange = useCallback((code, language, activeFile) => {
    const lines = code.split('\n').length;
    const lastSnapshot = snapshots[snapshots.length - 1];

    // Trigger on significant changes
    if (!lastSnapshot ||
        Math.abs(lines - (lastSnapshot.code?.split('\n').length || 0)) > 5 ||
        !lastSnapshot.code ||
        activeFile?.file?.name !== lastSnapshot.file) {

      scheduleAutoAnalysis(code, language, activeFile);
    }
  }, [snapshots, scheduleAutoAnalysis]);

  // Manual refresh
  const refreshFeedback = useCallback((code, language, activeFile) => {
    triggerAnalysis(code, language, activeFile, 'manual');
  }, [triggerAnalysis]);

  // Clear feedback
  const clearFeedback = useCallback(() => {
    setFeedbackItems([]);
    setError(null);
  }, []);

  // Toggle feedback pane
  const toggleFeedback = useCallback(() => {
    setShowFeedback(prev => !prev);
  }, []);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (analysisTimer.current) {
        clearTimeout(analysisTimer.current);
      }
    };
  }, []);

  return {
    // State
    feedbackItems,
    loading,
    error,
    showFeedback,
    snapshots,

    // Actions
    triggerAnalysis,
    refreshFeedback,
    clearFeedback,
    toggleFeedback,
    setShowFeedback,
    analyzeCodeChange,

    // Utils
    hasFeedback: feedbackItems.length > 0
  };
};

export default useAIFeedback;